/**
 * Configuration centrale du système multilingue KelasAI.
 * Source unique de vérité pour les locales supportées, la langue par défaut
 * et la stratégie de repli (fallback).
 */

export const LANGUAGE_STORAGE_KEY = "kelas-i18n-lang";

export const DEFAULT_LOCALE = "fr" as const;
export const FALLBACK_LOCALE = "fr" as const;

export interface LocaleDefinition {
  /** Code court utilisé par i18next et le localStorage */
  code: string;
  /** Locale complète (Intl / TTS) */
  locale: string;
  /** Nom affiché dans le sélecteur de langue */
  label: string;
  flag: string;
  dir: "ltr" | "rtl";
}

export const SUPPORTED_LOCALES: LocaleDefinition[] = [
  { code: "fr", locale: "fr-FR", label: "Français", flag: "🇫🇷", dir: "ltr" },
  { code: "en", locale: "en-US", label: "English", flag: "🇬🇧", dir: "ltr" },
  { code: "es", locale: "es-ES", label: "Español", flag: "🇪🇸", dir: "ltr" },
  { code: "pt", locale: "pt-BR", label: "Português", flag: "🇧🇷", dir: "ltr" },
  { code: "de", locale: "de-DE", label: "Deutsch", flag: "🇩🇪", dir: "ltr" },
  { code: "it", locale: "it-IT", label: "Italiano", flag: "🇮🇹", dir: "ltr" },
  { code: "ar", locale: "ar-SA", label: "العربية", flag: "🇸🇦", dir: "rtl" },
  { code: "tr", locale: "tr-TR", label: "Türkçe", flag: "🇹🇷", dir: "ltr" },
  { code: "zh", locale: "zh-CN", label: "中文", flag: "🇨🇳", dir: "ltr" },
  { code: "ru", locale: "ru-RU", label: "Русский", flag: "🇷🇺", dir: "ltr" },
];

export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LOCALES.map((l) => l.code);

export function isSupportedLanguage(code: string | null | undefined): boolean {
  if (!code) return false;
  return SUPPORTED_LANGUAGE_CODES.includes(code.split("-")[0]);
}

/** Normalise n'importe quelle entrée ("fr-FR", "FR", "fr") vers un code supporté. */
export function normalizeLanguage(code: string | null | undefined): string {
  if (!code) return DEFAULT_LOCALE;
  const short = code.toLowerCase().split("-")[0];
  return SUPPORTED_LANGUAGE_CODES.includes(short) ? short : DEFAULT_LOCALE;
}

export function getLocaleDefinition(code: string): LocaleDefinition {
  const short = normalizeLanguage(code);
  return (
    SUPPORTED_LOCALES.find((l) => l.code === short) ?? SUPPORTED_LOCALES[0]
  );
}
